import React from 'react';
import { FiDollarSign, FiTrendingUp, FiSmartphone } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount,
  TextArea
} from '../common/FormStyles';
import { Candidate } from '@/models';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { INFORMAL_INCOME_DOCUMENTS } from '@/config/documentsBySection';
import { calculateTotalRevenuesEmployee } from '@/utils/financialCalculations';

interface InformalIncomeSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
}


export const InformalIncomeSection: React.FC<InformalIncomeSectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const [incomeTab, setIncomeTab] = React.useState<'jour' | 'semaine' | 'mois'>(
    data.type_revenu_saisi ?? 'mois'
  );

  // Calculer la valeur à afficher en fonction de l'onglet actif
  const calculateDisplayValue = (
    savedType: 'jour' | 'semaine' | 'mois' | null,
    savedAmount: number | null,
    displayType: 'jour' | 'semaine' | 'mois'
  ): number | '' => {
    if (!savedAmount || !savedType) return '';
    if (savedType === displayType) return savedAmount;

    // Conversion vers le type d'affichage
    const workDays = data.jours_travail || [];
    const numberOfWorkDays = workDays.length;
    const daysPerMonth = numberOfWorkDays > 0 ? (numberOfWorkDays * 4.33) : 26;

    // Convertir d'abord en montant journalier
    let dailyAmount = 0;
    if (savedType === 'jour') {
      dailyAmount = savedAmount;
    } else if (savedType === 'semaine') {
      dailyAmount = savedAmount / (numberOfWorkDays || 5); // Diviser par jours de travail par semaine
    } else { // mois
      dailyAmount = savedAmount / daysPerMonth;
    }

    // Puis convertir vers le type d'affichage
    if (displayType === 'jour') {
      return Math.round(dailyAmount);
    } else if (displayType === 'semaine') {
      return Math.round(dailyAmount * (numberOfWorkDays || 5));
    } else { // mois
      return Math.round(dailyAmount * daysPerMonth);
    }
  };

  
  

  const [incomeValue, setIncomeValue] = React.useState<number | ''>(
    data.montant_revenu_saisi ?? 0
  );

  // Synchroniser incomeValue quand on change d'onglet ou que les données changent
  React.useEffect(() => {
    const displayValue = calculateDisplayValue(data.type_revenu_saisi ?? null, data.montant_revenu_saisi ?? null, incomeTab);
    setIncomeValue(displayValue);
  }, [incomeTab, data.montant_revenu_saisi, data.type_revenu_saisi, data.jours_travail]);

  const handleIncomeChange = (type: 'jour' | 'semaine' | 'mois', value: number) => {

    console.log('data.montant_revenu_saisi',data.montant_revenu_saisi);
    console.log('data.monthly_income',data.monthly_income);
    setIncomeValue(value);
    

    // Calculer le revenu mensuel estimé en fonction des jours de travail effectifs
    let monthlyEquivalent = 0;
    const workDays = data.jours_travail || [];
    const numberOfWorkDays = workDays.length;

    if (type === 'jour') {
      // Si jours_travail est défini, utiliser le nombre réel de jours
      // Sinon, estimer à 26 jours par mois (5.2 semaines * 5 jours)
      const daysPerMonth = numberOfWorkDays > 0
        ? (numberOfWorkDays * 4.33) // 4.33 semaines par mois en moyenne
        : 26; // Estimation par défaut (environ 5-6 jours/semaine)
      monthlyEquivalent = (value || 0) * daysPerMonth;
    } else if (type === 'semaine') {
      // 4.33 semaines par mois en moyenne
      monthlyEquivalent = (value || 0) * 4.33;
    } else {
      monthlyEquivalent = value || 0;
    }


    handleChange('montant_revenu_saisi', Number(value));
    handleChange('monthly_income', Math.round(monthlyEquivalent));
  };

  const handlePaymentMethodChange = (method: string, percentage: number) => {
    const methods: Record<string, number> = { ...(data.methodes_paiement || {}) };
    methods[method] = percentage;

    // Ensure total doesn't exceed 100
    const total = Object.values(methods).reduce((sum: number, val: any) => sum + (val || 0), 0);
    if (total <= 100) {
      handleChange('methodes_paiement', methods);
    }
  };

  const paymentMethodTotal = Object.values(data.methodes_paiement || {}).reduce(
    (sum: number, val: any) => sum + (val || 0),
    0
  );


    // Calcul automatique du total des revenus utilisant la fonction utilitaire centralisée
    const totalRevenus = data ? calculateTotalRevenuesEmployee(data) : 0;

  
    // Mettre à jour le total calculé
    React.useEffect(() => {
   
        handleChange('total_revenus', totalRevenus);
      }
    , [totalRevenus, data]);

  return (
    <FormSection>
      <SectionTitle>
        <FiDollarSign />
        Vos Revenus
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Indiquez vos revenus de manière honnête. Ces informations restent confidentielles
          et nous permettent d'évaluer votre capacité à payer le loyer.
        </p>
      </InfoBox>

      <SubSectionTitle>Revenus de votre activité principale</SubSectionTitle>

      <FormGroup>
        <Label $required>En moyenne, combien gagnez-vous ?</Label>
        <div style={{ marginBottom: '10px' }}>
          <button
            type="button"
            onClick={() => {setIncomeTab('jour'); handleChange('type_revenu_saisi', 'jour')}}
            disabled={readonly}
            style={{
              padding: '8px 16px',
              marginRight: '8px',
              backgroundColor: incomeTab === 'jour' ? '#2563eb' : '#e5e7eb',
              color: incomeTab === 'jour' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Par jour
          </button>
          <button
            type="button"
            onClick={() => {setIncomeTab('semaine'); handleChange('type_revenu_saisi', 'semaine')}}
            disabled={readonly}
            style={{
              padding: '8px 16px',
              marginRight: '8px',
              backgroundColor: incomeTab === 'semaine' ? '#2563eb' : '#e5e7eb',
              color: incomeTab === 'semaine' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Par semaine
          </button>
          <button
            type="button"
            onClick={() => {setIncomeTab('mois'); handleChange('type_revenu_saisi', 'mois')}}
            disabled={readonly}
            style={{
              padding: '8px 16px',
              backgroundColor: incomeTab === 'mois' ? '#2563eb' : '#e5e7eb',
              color: incomeTab === 'mois' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Par mois
          </button>
        </div>

        <Input
          type="number"
          min="0"
          value={incomeValue}
          onChange={e => {
            handleIncomeChange(incomeTab, parseInt(e.target.value))}}
          placeholder={`Montant ${incomeTab === 'jour' ? 'journalier' : incomeTab === 'semaine' ? 'hebdomadaire' : 'mensuel'} (FCFA)`}
        />
        <HelpText>
          {incomeTab === 'jour' && 'Combien gagnez-vous en moyenne par jour ?'}
          {incomeTab === 'semaine' && 'Combien gagnez-vous en moyenne par semaine ?'}
          {incomeTab === 'mois' && 'Combien gagnez-vous en moyenne par mois ?'}
        </HelpText>
      </FormGroup>

      {data.monthly_income && data.monthly_income > 0 && (
        <>
          <CalculatedValue>
            <CalculatedLabel>Équivalent mensuel estimé</CalculatedLabel>
            <CalculatedAmount>{data.monthly_income.toLocaleString()} FCFA</CalculatedAmount>
          </CalculatedValue>
          {incomeTab === 'jour' && (
            <HelpText style={{ marginTop: '8px', fontStyle: 'italic' }}>
              {data.jours_travail && data.jours_travail.length > 0
                ? `Calculé sur ${data.jours_travail.length} jour(s) de travail par semaine × 4.33 semaines`
                : 'Calculé sur une estimation de 26 jours de travail par mois'}
            </HelpText>
          )}
          {incomeTab === 'semaine' && (
            <HelpText style={{ marginTop: '8px', fontStyle: 'italic' }}>
              Calculé sur 4.33 semaines par mois en moyenne
            </HelpText>
          )}
        </>
      )}

      <SubSectionTitle>
        <FiTrendingUp style={{ fontSize: '18px' }} />
        Variation des revenus
      </SubSectionTitle>

      <FormGroup>
        <Label>Vos revenus varient-ils selon les périodes ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.revenus_variables || false}
            onChange={e => handleChange('revenus_variables', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.revenus_variables ? 'Oui, mes revenus varient' : 'Non, revenus stables'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.revenus_variables && (
        <>
          <FormGrid>
            <FormGroup>
              <Label>Meilleure période</Label>
              <Input
                type="text"
                value={data.meilleure_periode || ''}
                onChange={e => handleChange('meilleure_periode', e.target.value)}
                placeholder="Ex: Décembre, Rentrée scolaire..."
                disabled={readonly}
              />
            </FormGroup>

            <FormGroup>
              <Label>Montant mensuel (FCFA)</Label>
              <Input
                type="number"
                min="0"
                value={data.montant_meilleure_periode || ''}
                onChange={e => handleChange('montant_meilleure_periode', parseInt(e.target.value))}
                placeholder="Ex: 200000"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label>Période difficile</Label>
              <Input
                type="text"
                value={data.periode_difficile || ''}
                onChange={e => handleChange('periode_difficile', e.target.value)}
                placeholder="Ex: Juillet-Août, Saison des pluies..."
                disabled={readonly}
              />
            </FormGroup>

            <FormGroup>
              <Label>Montant mensuel (FCFA)</Label>
              <Input
                type="number"
                min="0"
                value={data.montant_periode_difficile || ''}
                onChange={e => handleChange('montant_periode_difficile', parseInt(e.target.value))}
                placeholder="Ex: 80000"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>
        </>
      )}




         <SubSectionTitle>Autres Revenus Mensuels</SubSectionTitle>
      
            
      
              <FormGrid>
                <FormGroup>
                  <Label>Transferts de la diaspora (FCFA)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={data?.transferts_diaspora || ''}
                    onChange={e => handleChange('transferts_diaspora', parseInt(e.target.value) || 0)}
                    placeholder="Ex: 30000"
                    disabled={readonly}
                  />
                  <HelpText>Moyenne mensuelle des transferts reçus de l'étranger</HelpText>
                </FormGroup>
      
                <FormGroup>
                  <Label>Autres revenus (FCFA)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={data?.autres_revenus || ''}
                    onChange={e => handleChange('autres_revenus', parseInt(e.target.value) || 0)}
                    placeholder="Ex: 20000"
                    disabled={readonly}
                  />
                </FormGroup>
              </FormGrid>
      
              {(data?.autres_revenus || 0) > 0 && (
                <FormGroup>
                  <Label>Description des autres revenus</Label>
                  <TextArea
                    value={data?.description_autres_revenus || ''}
                    onChange={e => handleChange('description_autres_revenus', e.target.value)}
                    placeholder="Décrivez la source de ces autres revenus..."
                    disabled={readonly}
                  />
                </FormGroup>
              )}
      
              <CalculatedValue>
                <CalculatedLabel>Total de vos revenus nets mensuels</CalculatedLabel>
                <CalculatedAmount>{totalRevenus.toLocaleString()} FCFA</CalculatedAmount>
              </CalculatedValue>








      <SubSectionTitle>Méthodes de paiement</SubSectionTitle>

      <FormGroup>
        <Label>Comment recevez-vous vos paiements ?</Label>
        <HelpText>
          Indiquez la répartition en pourcentage (le total doit faire 100%)
        </HelpText>
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label>Espèces (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={(data.methodes_paiement?.especes) || ''}
            onChange={e => handlePaymentMethodChange('especes', parseInt(e.target.value) || 0)}
            placeholder="Ex: 60"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Mobile Money (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={(data.methodes_paiement?.mobile_money) || ''}
            onChange={e => handlePaymentMethodChange('mobile_money', parseInt(e.target.value) || 0)}
            placeholder="Ex: 30"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Virements bancaires (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={(data.methodes_paiement?.virement) || ''}
            onChange={e => handlePaymentMethodChange('virement', parseInt(e.target.value) || 0)}
            placeholder="Ex: 10"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Troc / Échange (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={(data.methodes_paiement?.troc) || ''}
            onChange={e => handlePaymentMethodChange('troc', parseInt(e.target.value) || 0)}
            placeholder="Ex: 0"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      {paymentMethodTotal > 0 && (
        <CalculatedValue>
          <CalculatedLabel>Total</CalculatedLabel>
          <CalculatedAmount
            style={{ color: paymentMethodTotal === 100 ? '#10b981' : '#ef4444' }}
          >
            {paymentMethodTotal}%
            {paymentMethodTotal !== 100 && ' (doit faire 100%)'}
          </CalculatedAmount>
        </CalculatedValue>
      )}

      <SubSectionTitle>
        <FiSmartphone style={{ fontSize: '18px' }} />
        Utilisation de Mobile Money
      </SubSectionTitle>

      <FormGroup>
        <Label>Utilisez-vous Mobile Money ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.utilise_mobile_money || false}
            onChange={e => handleChange('utilise_mobile_money', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.utilise_mobile_money ? 'Oui, j\'utilise Mobile Money' : 'Non'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.utilise_mobile_money && (
        <>
          <FormGrid>
            <FormGroup>
              <Label>Depuis combien de temps ?</Label>
              <Select
                value={data.depuis_quand_mobile_money || ''}
                onChange={e => handleChange('depuis_quand_mobile_money', e.target.value)}
                disabled={readonly}
              >
                <option value="">Sélectionnez...</option>
                <option value="Moins de 6 mois">Moins de 6 mois</option>
                <option value="6-12 mois">6 à 12 mois</option>
                <option value="1-2 ans">1 à 2 ans</option>
                <option value="Plus de 2 ans">Plus de 2 ans</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Opérateur principal</Label>
              <Select
                value={data.operateur_mobile_money || ''}
                onChange={e => handleChange('operateur_mobile_money', e.target.value)}
                disabled={readonly}
              >
                <option value="">Sélectionnez...</option>
                <option value="Orange Money">Orange Money</option>
                <option value="MTN Money">MTN Money</option>
                <option value="Moov Money">Moov Money</option>
              </Select>
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label>Montant moyen reçu par mois via Mobile Money (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.montant_mensuel_mobile_money || ''}
              onChange={e => handleChange('montant_mensuel_mobile_money', parseInt(e.target.value))}
              placeholder="Ex: 150000"
              disabled={readonly}
            />
            <HelpText>
              Montant total que vous recevez en moyenne chaque mois sur votre compte Mobile Money
            </HelpText>
          </FormGroup>
        </>
      )}

      <InfoBox $type="warning">
        <p>
          <strong>Important :</strong> Les relevés Mobile Money peuvent servir de preuve de vos revenus.
          Si vous les avez, vous pourrez les fournir dans la section "Preuves de votre activité".
        </p>
      </InfoBox>


 {onDocumentUpload && onDocumentRemove && (
            <DocumentUpload
              sectionTitle="Vos Revenus"
              documents={INFORMAL_INCOME_DOCUMENTS}
              uploadedFiles={uploadedDocuments}
              onFileSelect={onDocumentUpload}
              onFileRemove={onDocumentRemove}
              readonly={readonly}
            />
          )}
      
    </FormSection>
  );
};
